<script setup lang="ts">
import Button from "primevue/button";
import { Participant, ParticipantActivityType, ParticipantStatus, Project, Trainer } from "../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI } from "../../api";
import { sendProfileEmail } from "../../utils/EmailUtils.ts";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";
import { bringFocusToElementById } from "../../utils/GeneralUtils.ts";
import { computed } from "vue";

const props = defineProps({
    project: {
        type: Object as () => Project,
        required: true
    },
    participant: {
        type: Object as () => Participant | undefined,
        required: false,
        default: undefined
    },
    trainer: {
        type: Object as () => Trainer,
        required: true
    },
    withLabel: {
        type: Boolean,
        default: false
    },
    withClasses: {
        type: String,
        default: "",
    },
});

const emits = defineEmits(["releasedParticipant", "releasedParticipants"]);

const { t } = useI18n();
const confirm = useConfirm();

async function shareProfile() {
    if (props.participant) {
        const participant = await ProfileAdministrationAPI.updateParticipant({
            id: props.participant.id,
            status: ParticipantStatus.NOTIFIED,
            gender: props.participant.gender,
            email: props.participant.email,
            firstName: props.participant.firstName,
            name: props.participant.name,
            questionnaire: props.participant.questionnaire,
            projectId: props.participant.projectId,
            selectedProduct: props.participant.selectedProduct,
            departmentId: props.participant.departmentId,
            roleId: props.participant.roleId,
            deletionDate: props.participant.deletionDate,
            score: props.participant.score,
        });

        await sendProfileEmail(props.trainer, participant, props.project);
        await ProfileAdministrationAPI.logParticipantActivity(participant.id!, ParticipantActivityType.PROFILE_SHARED);
        emits("releasedParticipant", participant);
    } else if (props.project?.id) {
        // Anonymous participants (pseudonym, usually no address) never receive their profile.
        const participants = (await ProfileAdministrationAPI.getParticipants(props.project.id))
            .filter((participant) => participant.status === "GENERATED" && !participant.anonymous);
        if (participants.length === 0) { return }
        const releasedParticipants: Participant[] = [];
        await Promise.all(
            participants.map(async (p) => {
                try {
                    const updated = await ProfileAdministrationAPI.updateParticipant({
                        id: p.id,
                        status: ParticipantStatus.NOTIFIED,
                        gender: p.gender,
                        email: p.email,
                        firstName: p.firstName,
                        name: p.name,
                        questionnaire: p.questionnaire,
                        projectId: p.projectId,
                        selectedProduct: p.selectedProduct,
                        departmentId: p.departmentId,
                        roleId: p.roleId,
                        deletionDate: p.deletionDate,
                        score: p.score,
                    });

                    await sendProfileEmail(props.trainer, updated, props.project);
                    await ProfileAdministrationAPI.logParticipantActivity(
                        updated.id!,
                        ParticipantActivityType.PROFILE_SHARED
                    );

                    releasedParticipants.push(updated);
                } catch (error) {
                    console.error(`Failed to process participant ${p.id}:`, error);
                }
            })
        );
        emits("releasedParticipants", releasedParticipants);
    }
}

/**
 * Show a confirmation dialog before sharing a profile.
 */
const idSuffix = computed(() => {
    if (props.participant !== undefined) {
        return props.participant.id;
    }
    return props.withLabel ? "" : "Quick";
});

const shareConfirm = () => {
    const displayName = props.participant?.firstName && props.participant?.name
        ? `${props.participant.firstName} ${props.participant.name}`
        : props.participant?.email;

    const message = props.participant === undefined
        ? t("project-details.participants.share.message.all")
        : t("project-details.participants.share.message.single", { displayName });

    confirm.require({
        group: `shareConfirmationDialog${idSuffix.value}`,
        message,
        header: t("project-details.participants.share.heading"),
        acceptLabel: t("buttons.share"),
        accept: shareProfile
    });
};

/**
 * Accessibility: Brings focus to the share button in the dialog when it is shown.
 */
function bringFocusToDialog() {
    bringFocusToElementById(`profile-release-btn_cancel`);
}
</script>

<template>
    <Button
        icon="pi pi-file-export"
        :class="withClasses"
        @click="shareConfirm"
        :label="withLabel ? t('project-details.participants.callForAction.share') : ''"
        :outlined="!withLabel && !props.participant"
        v-tooltip.top="{ value: props.participant ? t('project-details.participants.tooltips.share.single') : t('project-details.participants.tooltips.share.all'), showDelay: 500 }"
        :id="participant ? `profile-release-button_share-profile-${participant.id}` : withLabel ? 'profile-release-button_share-profile' : 'profile-release-button_share-profile-quick'"
    ></Button>
    <ConfirmDialog
        :group="participant ? 'shareConfirmationDialog' + participant.id : withLabel ? 'shareConfirmationDialog' : 'shareConfirmationDialogQuick'"
        :dismissable-mask="true"
        :block-scroll="true"
        :draggable="false"
        :id="participant ? 'shareConfirmationDialog' + participant.id : withLabel ? 'shareConfirmationDialog' : 'shareConfirmationDialogQuick'"
        @show="bringFocusToDialog()"
    >
        <template #container="{ message: dialogMessage, acceptCallback, rejectCallback }">
            <div class="flex flex-col items-center p-8 bg-surface rounded">
                <div
                    class="rounded-full bg-brand text-brand-contrast inline-flex justify-center items-center h-24 w-24 -mt-20"
                >
                    <i class="pi pi-question text-5xl text-white"></i>
                </div>
                <span class="font-bold text-2xl block mb-2 mt-6">{{ dialogMessage.header }}</span>
                <p class="mb-0">{{ dialogMessage.message }}</p>
                <div class="flex items-center gap-2 mt-6">
                    <Button
                        :id="participant ? `profile-release-btn_btn_confirm-${project.id}-${participant.id}` : withLabel ? `profile-release-btn_btn_confirm-${project.id}` : `profile-release-btn_btn_confirm-quick-${project.id}`"
                        :label="dialogMessage.acceptLabel"
                        @click="acceptCallback"
                    ></Button>
                    <Button :id="`profile-release-btn_cancel`" :label="t('buttons.cancel')" outlined @click="rejectCallback"></Button>
                </div>
            </div>
        </template>
    </ConfirmDialog>
</template>
